import React, { useRef, useMemo } from "react";
import { compose } from "recompose";
import { withTable, getStatus } from "components/common";
import { withXeroErrors, withDocumentRenderer } from "components";
import { Table as ReactstrapTable } from "reactstrap";
import { Trans, withTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { findMajorityCurrency, formatCurrency, getSelectedAmount } from "services/util";
import Search from "../../AccountsReceivable/Search";
import { BillDocument } from "components/documents";
import { BulkDraftActions } from "../../components/BulkDraftActions";
import { useOrganisationContext } from "hooks";
import { TruncateTd } from "components/common/Tables";

const EmailCell = withDocumentRenderer({
  onDownload: () => (getDocument, item) =>
    getDocument(
      {
        item,
      },
      `bill_${item.InvoiceNumber}.pdf`
    ),
  type: "BILLS",
})(({ EmailDocument, DocumentRenderer, i, selectedOrganisation }) => (
  <>
    <EmailDocument compact item={i} type="BILLS" />
    <DocumentRenderer component={BillDocument} selectedOrganisation={selectedOrganisation} />
  </>
));

const DownloadCell = withDocumentRenderer({
  onDownload: () => (getDocument, item) =>
    getDocument(
      {
        item,
      },
      `bill_${item.InvoiceNumber}.pdf`
    ),
  type: "BILLS",
})(({ DownloadDocument, DocumentRenderer, i, selectedOrganisation }) => (
  <>
    <DownloadDocument compact item={i} className="mr-1" />
    <DocumentRenderer component={BillDocument} selectedOrganisation={selectedOrganisation} />
  </>
));

export const Table = ({
  XeroErrors,
  Pagination,
  TableContainer,
  ViewingRecords,
  Loader,
  XeroTableDataFetch,
  data,
  setFilters,
  DocumentRenderer,
  DownloadDocument,
  status,
  t,
  filters,
  refreshData,
  selected,
  calculationTotal,
}) => {
  const initialFilters = useRef(filters);

  const selectedList = Object.keys(selected).filter(key => selected[key]);
  const selectedData = data?.results
    ?.filter(item => selectedList.includes(item.InvoiceID))
    ?.map(item => ({ Total: parseFloat(item.Total), SubTotal: parseFloat(item.SubTotal) }));
  const selectedDataList = useMemo(() => {
    return data?.results?.filter(item => selectedList.includes(item.InvoiceID));
  }, [data?.results, selectedList]);

  const totalTotal = formatCurrency(selectedData.reduce((acc, item) => acc + item.Total, 0));

  const majorityCurrency = findMajorityCurrency(data.results);
  const selectedTotalMessage = getSelectedAmount(selectedList?.length, "Bill", totalTotal, majorityCurrency);

  const { selectedOrganisation } = useOrganisationContext()


  return (
    <div className="inv-table">
      <XeroErrors color="warning" />

      <Search
        onFiltersChanged={(v) => {
          setFilters(v === null ? initialFilters.current : v);
        }}
        status={status}
        selectedItems={selectedDataList}
        isBill={true}
        selectedOrganisation={selectedOrganisation}
      />
      <div className="row my-3">
        <div className="col-6 text-left">
          {status === "DRAFT" && (
            <BulkDraftActions
              onSuccess={refreshData}
              items={data.results}
              url="invoices"
              pk="InvoiceID"
              selected={selected}
            />
          )}
        </div>
        <div className="col-6">
          <div className="text-muted text-right">
            <span>{selectedList.length > 0 ? (selectedTotalMessage) : (calculationTotal)}</span>
          </div>
        </div>
      </div>
      <div className="small">
        <TableContainer idKey="PurchaseOrderID" >
          <ReactstrapTable size="sm" className="small" responsive>
            <thead>
              <tr>
                <th select />
                <th sortBy="InvoiceNumber" className="text-nowrap">
                  <Trans>Reference</Trans>
                </th>
                <th sortBy="Status" className="text-nowrap">
                  <Trans>Status</Trans>
                </th>
                <th sortBy="Contact.Name" className="text-nowrap">
                  <Trans>From</Trans>
                </th>
                <th sortBy="Reference" className="text-nowrap">
                  <Trans>Note</Trans>
                </th>
                <th sortBy="Date" className="text-nowrap">
                  <Trans>Date</Trans>
                </th>
                <th sortBy="DueDate" className="text-nowrap">
                  <Trans>Due Date</Trans>
                </th>
                <th sortBy="AmountPaid" className="text-nowrap">
                  <Trans>Paid</Trans>
                </th>
                <th sortBy="AmountDue" className="text-nowrap">
                  <Trans>Due</Trans>
                </th>
                <th className="text-nowrap">
                  <Trans>Actions</Trans>
                </th>
                <th />
              </tr>
            </thead>
            <tbody>
              <Loader />
              {data.results.map((i, idx) => (
                <tr key={i.InvoiceID}>
                  <td select />
                  <TruncateTd content={i.InvoiceNumber || "-"} _key={`number_${idx}`} className="truncate">
                    <Link to={`/purchases/bills/view/${i.InvoiceID}`}>
                      {(i.InvoiceNumber || "-").toUpperCase()}
                    </Link>
                  </TruncateTd>
                  <td>
                    <Trans>{getStatus(i.Status)}</Trans>
                  </td>
                  <TruncateTd content={i.Contact?.Name} _key={`contact_${idx}`} className="truncate">
                    {i.Contact &&
                      <Link to={`/contacts/view/${i.Contact.ContactID}`} >
                        {i.Contact.Name}
                      </Link>
                    }
                  </TruncateTd>
                  <TruncateTd content={i.Reference || "-"} _key={`reference_${idx}`} className="truncate">
                    {i.Reference || "-"}
                  </TruncateTd>
                  <td>{i.DateString}</td>
                  <td>{i.DueDateString}</td>
                  <td>
                    {formatCurrency(i.AmountPaid)}
                  </td>
                  <td>
                    {formatCurrency(i.AmountDue)}
                  </td>
                  <td className="text-nowrap">
                    <DownloadCell i={i} selectedOrganisation={selectedOrganisation} />
                    <EmailCell i={i} selectedOrganisation={selectedOrganisation} />
                  </td>
                  <td className="text-success">
                    {i.SentToContact ? <Trans>Sent</Trans> : ""}
                  </td>

                </tr>
              ))}
            </tbody>
          </ReactstrapTable>
        </TableContainer>
      </div>
      <DocumentRenderer component={BillDocument} />
      <XeroTableDataFetch />
      <div className="float-right">
        <Pagination size="sm" noPages />
      </div>
      <div className="clearfix">
        <ViewingRecords noCount className="float-left" />
      </div>
    </div>
  );
};

export const enhance = compose(
  withTranslation(),
  withXeroErrors(),
  withTable({
    limit: 50,
    url: "invoices",
    ordering: {
      column: "Date",
      direction: 2,
    },
  }),
  withDocumentRenderer({
    onDownload:
      ({ xero }) =>
        (getDocument, item) => {
          getDocument(
            { item },
            `bill_${item.InvoiceNumber || item.Reference || item.Contact.Name
            }.pdf`
          );
        },
  })
);

export default enhance(Table);
